import { useStorage } from '@vueuse/core';
import type { SuiSortListItem } from '@developers/scoro-ui';
import {
	createDefaultColumnSettings,
	LOCKED_COLUMNS_END,
	LOCKED_COLUMNS_START,
	LOCKED_SORTABLE_COLUMNS_START,
	SORTABLE_COLUMN_IDS,
	LOCKED_COLUMNS_START_SET,
	LOCKED_COLUMNS_END_SET,
	STORAGE_KEY,
	COLUMNS_HIDDEN_BY_DEFAULT,
} from './constants';
import type { TaskListColumnSettings } from './types';
import { COLUMN_TRANSLATION_KEYS } from '../constants';
import { useTranslation } from '@/composables';

export interface TaskListColumnSettingsOptions {
	storageKey?: string;
	availableColumns?: readonly string[];
}

export const toFixedItems = (
	columnIds: readonly string[],
	translateFn: (key: string) => string
): SuiSortListItem[] => {
	return columnIds.map((id) => {
		const translationKey = COLUMN_TRANSLATION_KEYS[id];
		return {
			key: id,
			label: translationKey ? translateFn(translationKey) : '',
			isChecked: true,
		};
	});
};

export const toSortableItems = (
	settings: TaskListColumnSettings,
	translateFn: (key: string) => string
): SuiSortListItem[] => {
	return [...settings]
		.sort((a, b) => a.order - b.order)
		.map((col) => {
			const translationKey = COLUMN_TRANSLATION_KEYS[col.id];
			const translatedLabel = translationKey
				? translateFn(translationKey)
				: '';
			return {
				key: col.id,
				label: translatedLabel || col.id,
				isChecked: col.visible,
			};
		});
};

export const fromSortableItems = (
	items: SuiSortListItem[]
): TaskListColumnSettings => {
	return items.map((item, index) => ({
		id: item.key,
		visible: item.isChecked,
		order: index,
	}));
};

export const useTaskListColumnSettings = (
	options: TaskListColumnSettingsOptions = {}
) => {
	const { storageKey = STORAGE_KEY, availableColumns = SORTABLE_COLUMN_IDS } =
		options;

	const availableColumnsSet = new Set(availableColumns);
	const defaultSettings = createDefaultColumnSettings(
		availableColumns,
		COLUMNS_HIDDEN_BY_DEFAULT
	);
	const { t } = useTranslation();

	const validateSettings = (
		settings: TaskListColumnSettings
	): TaskListColumnSettings => {
		const validSettings = settings.filter((s) =>
			availableColumnsSet.has(s.id)
		);

		if (validSettings.length === 0) {
			return defaultSettings;
		}

		const settingsIds = new Set(validSettings.map((s) => s.id));
		const missingColumns = Array.from(availableColumns).filter(
			(id) => !settingsIds.has(id)
		);

		const maxOrder = Math.max(...validSettings.map((s) => s.order), -1);
		missingColumns.forEach((id, index) => {
			validSettings.push({
				id,
				visible: true,
				order: maxOrder + index + 1,
			});
		});

		return validSettings;
	};

	const settings = useStorage<TaskListColumnSettings>(
		storageKey,
		defaultSettings,
		localStorage,
		{
			mergeDefaults: false,
			serializer: {
				read: (raw: string) => {
					try {
						const parsed = JSON.parse(raw);
						return validateSettings(parsed);
					} catch {
						return defaultSettings;
					}
				},
				write: (value: TaskListColumnSettings) => {
					return JSON.stringify(value);
				},
			},
		}
	);

	const getFixedHeaderItems = (): SuiSortListItem[] => {
		return toFixedItems(LOCKED_SORTABLE_COLUMNS_START, t);
	};

	const getSortableList = (): SuiSortListItem[] => {
		return toSortableItems(settings.value, t);
	};

	const saveSettings = (sortedList: SuiSortListItem[]): boolean => {
		settings.value = fromSortableItems(sortedList);
		return true;
	};

	const resetToDefaults = () => {
		settings.value = defaultSettings;
	};

	const getOrderedColumnIds = (): string[] => {
		const sortableVisible = [...settings.value]
			.filter((s) => s.visible)
			.sort((a, b) => a.order - b.order)
			.map((s) => s.id);

		return [
			...LOCKED_COLUMNS_START,
			...sortableVisible,
			...LOCKED_COLUMNS_END,
		];
	};

	const isColumnVisible = (columnId: string): boolean => {
		if (
			LOCKED_COLUMNS_START_SET.has(columnId) ||
			LOCKED_COLUMNS_END_SET.has(columnId)
		) {
			return true;
		}

		const setting = settings.value.find((s) => s.id === columnId);
		return setting?.visible ?? true;
	};

	return {
		settings,
		getFixedHeaderItems,
		getSortableList,
		saveSettings,
		resetToDefaults,
		getOrderedColumnIds,
		isColumnVisible,
	};
};
